import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { getPostBySlug, getAllPosts, BlogPost } from '../lib/blog'
import { formatDate } from '../utils/date'
import BlogBreadcrumb from '../components/Blog/BlogBreadcrumb'

import { Header } from '@/components/landing/Header'
import { Footer } from '@/components/landing/Footer'
import { config } from '../../config'
import { useTranslation } from '@/hooks/useTranslation'

export default function BlogArticle() {
	const { t, i18n } = useTranslation()
	const { slug } = useParams<{ slug: string }>()
	const navigate = useNavigate()
	const [post, setPost] = useState<BlogPost | null>(null)
	const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([])
	const [loading, setLoading] = useState(true)
	const [activeHeading, setActiveHeading] = useState<string>('')

	// Scroll spy for table of contents using IntersectionObserver
	useEffect(() => {
		if (!post?.headings) {
			return
		}

		// Get all heading elements
		const headingElements = post.headings
			.map((heading) => {
				const id = heading
					.toLowerCase()
					.replace(/\./g, '') // Remove dots
					.replace(/\s+/g, '-') // Replace spaces with hyphens
				const element = document.getElementById(id)

				// Fallback: try to find h2 by text content
				if (!element) {
					const allH2 = document.querySelectorAll('h2')
					for (let h2 of allH2) {
						if (h2.textContent === heading) {
							return { id: heading, element: h2 }
						}
					}
				}

				return { id: heading, element }
			})
			.filter((item) => item.element)

		// Create intersection observer
		const observerOptions = {
			rootMargin: '-20% 0px -70% 0px', // This means the heading is "active" when it's in the top 30% of the viewport
			threshold: 0,
		}

		const observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					const headingText = post.headings.find((h) => {
						const id = h.toLowerCase().replace(/\./g, '').replace(/\s+/g, '-')
						return id === entry.target.id || entry.target.textContent === h
					})

					if (headingText) {
						setActiveHeading(headingText)
					}
				}
			})
		}, observerOptions)

		// Observe all heading elements
		headingElements.forEach(({ element }) => {
			if (element) {
				observer.observe(element)
			}
		})

		// Cleanup
		return () => {
			headingElements.forEach(({ element }) => {
				if (element) {
					observer.unobserve(element)
				}
			})
			observer.disconnect()
		}
	}, [post])

	useEffect(() => {
		const fetchPost = async () => {
			if (!slug) {
				navigate('/blog')
				return
			}

			try {
				const postData = await getPostBySlug(slug, i18n.language)
				if (!postData) {
					navigate('/blog')
					return
				}
				setPost(postData)

				const allPosts = await getAllPosts(i18n.language)
				const related = allPosts.filter((p) => p.slug !== slug).slice(0, 3)
				setRelatedPosts(related)
			} catch (error) {
				console.error('Error fetching post:', error)
				navigate('/blog')
			} finally {
				setLoading(false)
			}
		}

		fetchPost()
	}, [slug, navigate, i18n.language])

	if (loading) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
					<p className="text-gray-600">{t('blogArticle.loadingArticle')}</p>
				</div>
			</div>
		)
	}

	if (!post) {
		return null
	}

	return (
		<div className="min-h-screen w-full mx-auto relative bg-gradient-to-b from-gray-50 to-white">
			{/* Decorative shapes matching landing page */}
			<div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
				<div className="absolute top-20 left-10 w-64 h-64 rounded-full bg-blue-100 opacity-50 blur-3xl"></div>
				<div className="absolute top-40 right-20 w-72 h-72 rounded-full bg-purple-100 opacity-40 blur-3xl"></div>
				<div className="absolute bottom-40 left-1/4 w-80 h-80 rounded-full bg-indigo-100 opacity-30 blur-3xl"></div>
			</div>

			{/* Main content */}
			<div className="relative z-10">
				<Header />

				<article className="w-full px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pb-16 md:pb-24">
					{/* Breadcrumb */}
					<div className="py-4">
						<BlogBreadcrumb articleTitle={post.title} />
					</div>

					{/* Post Header */}
					<div className="max-w-4xl mx-auto flex flex-col gap-8 items-center text-center py-12">
						{/* Heading */}
						<header>
							<h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
								{post.title}
							</h1>
						</header>

						{/* Overview */}
						<div className="text-lg md:text-xl text-gray-600 max-w-3xl">
							{post.excerpt || t('blogArticle.defaultExcerpt')}
						</div>

						{/* Author */}
						<div className="flex gap-3 items-center">
							<a
								href="https://x.com/tarunyadav9761"
								target="_blank"
								rel="noopener noreferrer"
								className="hover:opacity-80 transition-opacity">
								<img
									src="https://unavatar.io/x/tarunyadav9761"
									alt="Tarun Yadav"
									className="w-12 h-12 rounded-full object-cover"
								/>
							</a>
							<div className="text-left text-sm">
								<a
									href="https://x.com/tarunyadav9761"
									target="_blank"
									rel="noopener noreferrer"
									className="font-medium text-gray-900 hover:text-blue-600">
									Tarun Yadav
								</a>
								<div className="text-gray-500">
									<span className="mr-1">{t('blogArticle.updatedOn')}</span>
									<time dateTime={post.date}>{formatDate(post.date)}</time>
								</div>
							</div>
						</div>
					</div>

					{/* Cover Image */}
					<div className="my-12 md:my-4">
						<figure className="aspect-[2/1] md:aspect-[8/5] block overflow-hidden">
							<img
								src={post.cover || "/images/blog/Whisk_7360f2eafe.jpg"}
								alt={post.title}
								className="w-full h-full rounded-2xl md:rounded-3xl object-cover"
							/>
						</figure>
					</div>

					{/* 3-Column Layout */}
					<div className="relative w-full flex gap-12">
						{/* Left Sidebar - Table of Contents */}
						<aside
							id="left-sidebar"
							className="w-48 hidden lg:block flex-shrink-0">
							<div className="sticky top-20">
								<div className="p-6">
									<div className="text-sm font-semibold text-gray-900 mb-4">
										{t('blogArticle.contents')}
									</div>
									<ul className="space-y-2">
										{(post.headings || []).map((heading, index) => {
											const isActive = activeHeading === heading
											// Match the ID format from the HTML
											const headingId = heading
												.toLowerCase()
												.replace(/\./g, '') // Remove dots
												.replace(/\s+/g, '-') // Replace spaces with hyphens

											return (
												<li key={index}>
													<a
														href={`#${headingId}`}
														className={`text-sm transition-all duration-200 block pl-3 py-1 border-l-2 ${
															isActive
																? 'text-purple-600 font-medium border-purple-600 bg-purple-50'
																: 'text-gray-600 hover:text-purple-600 border-transparent hover:border-gray-300'
														}`}
														onClick={(e) => {
															e.preventDefault()
															// First try the formatted ID
															let element = document.getElementById(headingId)

															// If not found, try to find by text content
															if (!element) {
																const allH2 = document.querySelectorAll('h2')
																for (let h2 of allH2) {
																	if (h2.textContent === heading) {
																		element = h2
																		break
																	}
																}
															}

															if (element) {
																const top = element.offsetTop - 80
																window.scrollTo({ top, behavior: 'smooth' })
															}
														}}>
														{heading}
													</a>
												</li>
											)
										})}
									</ul>
								</div>
							</div>
						</aside>

						{/* Main Content */}
						<div className="flex-1 min-w-0">
							{/* Post Content - No card background */}
							<div className="p-8 md:p-12">
								<div
									className="prose prose-lg max-w-none prose-headings:font-bold prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6 prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4 prose-p:text-gray-700 prose-p:leading-relaxed prose-ul:my-6 prose-li:my-2"
									dangerouslySetInnerHTML={{ __html: post.content }}
								/>
							</div>
						</div>

						{/* Right Sidebar - Promotion */}
						<aside
							id="right-sidebar"
							className="w-64 hidden lg:block flex-shrink-0">
							<div className="sticky top-20">
								<div className="bg-gradient-to-br from-purple-600 to-blue-600 rounded-2xl p-6 text-white shadow-xl">
									<div className="space-y-4">
										{/* Logo */}
										<div className="flex items-center justify-start">
											<img
												src={config.logo}
												alt="Feedbask Logo"
												className="h-10 w-auto"
											/>
										</div>

										<div>
											<span className="text-xs font-semibold text-white/90 uppercase tracking-wide">
												{t('blogArticle.startFreeToday')}
											</span>
											<h3 className="text-xl font-bold text-white mt-2 leading-tight">
												{t('blogArticle.collectBetterFeedback')}
											</h3>
										</div>

										<p className="text-sm text-white/90 leading-relaxed">
											{t('blogArticle.joinHundredsOfTeams')}
										</p>

										<div className="space-y-3">
											<a
												href="/auth"
												className="block w-full text-center px-6 py-3 bg-white text-purple-600 hover:bg-gray-100 rounded-lg font-semibold transition-all transform hover:scale-105 shadow-lg">
												{t('blogArticle.startFreeTrial')}
											</a>
										</div>
									</div>
								</div>
							</div>
						</aside>
					</div>

					<div className="border-t border-gray-200 my-16"></div>

					{/* Related Posts */}
					{relatedPosts.length > 0 && (
						<div className="mt-14">
							<h2 className="text-3xl font-bold text-gray-900 mb-12">
								{t('blogArticle.morePosts')}
							</h2>
							<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
								{relatedPosts.map((relatedPost) => (
									<article key={relatedPost.slug} className="group">
										{/* Cover Image */}
										<figure className="mb-4">
											<Link
												to={`/blog/${relatedPost.slug}`}
												className="block rounded-2xl overflow-hidden">
												<img
													src={relatedPost.cover || "/images/blog/Whisk_7360f2eafe.jpg"}
													alt={relatedPost.title}
													className="w-full aspect-[8/5] object-cover transition-transform duration-500 group-hover:scale-105"
												/>
											</Link>
										</figure>
										<div className="space-y-3">
											{/* Date */}
											<p className="text-sm text-gray-500">
												<time dateTime={relatedPost.date}>
													{formatDate(relatedPost.date)}
												</time>
											</p>
											<div className="space-y-2">
												{/* Blog Heading */}
												<header>
													<h2 className="text-xl font-bold text-gray-900">
														<Link
															to={`/blog/${relatedPost.slug}`}
															className="group-hover:text-purple-600 transition-colors">
															{relatedPost.title}
														</Link>
													</h2>
												</header>
												{/* Excerpt */}
												<p className="text-gray-600 line-clamp-2">
													{relatedPost.excerpt || t('blogArticle.defaultRelatedExcerpt')}
												</p>
											</div>
										</div>
									</article>
								))}
							</div>
						</div>
					)}
				</article>
			</div>

			<Footer />
		</div>
	)
}
